import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Profile.css';
import { API_URL } from '../utils/config';

const Profile = () => {
  const { user, isAuthenticated, token, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  const [stats, setStats] = useState({
    totalOrders: 0,
    savedAddresses: 0,
    wishlistItems: 0
  });
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: ''
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        phone: user.phone || '',
        email: user.email || ''
      });
      setImagePreview(getImageUrl(user.profileImage));
      
      setStats(prev => ({
        ...prev,
        savedAddresses: user.addresses?.length || 0
      }));
    }

    fetchOrderCount();
  }, [user, isAuthenticated, navigate]);

  const fetchOrderCount = async () => {
    try {
      // FIXED: Changed from single quotes to backticks
      const response = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(prev => ({
        ...prev,
        totalOrders: response.data.length || 0
      }));
    } catch (error) {
      console.error('Error fetching order count:', error);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/img/default-avatar.png';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('phone', formData.phone);
      
      if (profileImage) {
        formDataToSend.append('profileImage', profileImage);
      }

      // FIXED: Using axios directly with proper URL
      const response = await axios.put(`${API_URL}/api/users/profile`, formDataToSend, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data) {
        updateUser(response.data);
        setMessage({ text: 'Profile updated successfully!', type: 'success' });
        setIsEditing(false);
        setProfileImage(null);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ text: 'Error updating profile', type: 'error' });
    }
    
    setLoading(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      fullName: user.fullName || '',
      phone: user.phone || '',
      email: user.email || ''
    });
    setImagePreview(getImageUrl(user.profileImage));
    setProfileImage(null);
    setMessage({ text: '', type: '' });
  };

  if (!user) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <section className="profile-header">
        <div className="profile-header-content">
          <h2>My Account</h2>
          <p>Manage your profile information and settings</p>
        </div>
      </section>

      <div className="profile-container section-p1">
        <div className="profile-sidebar">
          <div className="profile-menu">
            <Link to="/profile" className="profile-menu-item active">
              <i className="fas fa-user"></i>
              <span>My Profile</span>
            </Link>
            <Link to="/orders" className="profile-menu-item">
              <i className="fas fa-shopping-bag"></i>
              <span>My Orders</span>
            </Link>
            <Link to="/address-book" className="profile-menu-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Address Book</span>
            </Link>
          </div>
        </div>

        <div className="profile-content">
          {message.text && (
            <div className={`profile-message ${message.type}`}>
              <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
              {message.text}
            </div>
          )}

          <div className="profile-card">
            <div className="profile-card-header">
              <h3>
                <i className="fas fa-user-circle"></i>
                Personal Information
              </h3>
              {!isEditing && (
                <button 
                  className="btn-edit-profile"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fas fa-edit"></i>
                  Edit Profile
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="profile-form">
              <div className="profile-image-section">
                <div className="profile-image-container">
                  <img 
                    src={imagePreview} 
                    alt={formData.fullName}
                    className="profile-image-large"
                    onError={(e) => {
                      e.target.src = '/img/default-avatar.png';
                    }}
                  />
                  {isEditing && (
                    <label className="profile-image-upload">
                      <i className="fas fa-camera"></i>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
                <div className="profile-image-info">
                  <h4>{formData.fullName}</h4>
                  <p>{formData.email}</p>
                  <span className="profile-badge">
                    <i className="fas fa-check-circle"></i>
                    Verified Account
                  </span>
                </div>
              </div>

              <div className="profile-form-grid">
                <div className="form-group">
                  <label>
                    <i className="fas fa-user"></i>
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-envelope"></i>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                    title="Email cannot be changed"
                  />
                  <small className="form-hint">Email cannot be changed</small>
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-phone"></i>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-calendar"></i>
                    Member Since
                  </label>
                  <input
                    type="text"
                    value={new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    disabled
                  />
                </div>
              </div>

              {isEditing && (
                <div className="profile-form-actions">
                  <button 
                    type="submit" 
                    className="btn-save"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        Save Changes
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>
                </div>
              )}
            </form>
          </div>

          <div className="profile-stats">
            <Link to="/orders" className="stat-card" style={{ textDecoration: 'none' }}>
              <div className="stat-icon">
                <i className="fas fa-shopping-bag"></i>
              </div>
              <div className="stat-info">
                <h4>{stats.totalOrders}</h4>
                <p>Total Orders</p>
              </div>
            </Link>

            <Link to="/address-book" className="stat-card" style={{ textDecoration: 'none' }}>
              <div className="stat-icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="stat-info">
                <h4>{stats.savedAddresses}</h4>
                <p>Saved Addresses</p>
              </div>
            </Link>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-heart"></i>
              </div>
              <div className="stat-info">
                <h4>{stats.wishlistItems}</h4>
                <p>Wishlist Items</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;